import React, { useState, useRef, useEffect, useContext } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { ErrorPopup } from "../error/ErrorPopup";
import styles from "../../styles/components/UI/DropdownSelector.module.css";
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';

const translations = {
    ru: {
        placeholder: "Выберите...",
        noData: "Данных нет", 
    },
    en: {
        placeholder: "Select...",
        noData: "No data available", 
    },
};

export const DropdownSelector = ({
    filterName,
    filterKey,
    selectedValue,
    handleFilterChange,
    options = [],
    hasError,
    errorMessage,
    onFocus,
    onBlur,
    disabled = false, 
    shouldAutoClearOnMissingOptions = true,
}) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;

    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const [isMouseEnter, setIsMouseEnter] = useState(false);
    const hasOptions = options.length > 0; 
    const isDisabled = disabled || !hasOptions;

    const handleMouseEnter = () => {
        setIsMouseEnter(true);
    };

    const handleMouseLeave = () => {
        setIsMouseEnter(false);
    };

    const toggleDropdown = () => {
        if (isDisabled) return; 

        if (hasOptions) { 
            setIsOpen(!isOpen);
            if (!isOpen && onFocus) onFocus(filterName);
        }
    };

    const getOptionLabel = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('label') ? option.label : option;
    };

    const getOptionValue = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('value') ? option.value : option;
    };

    const getOptionDescription = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('description') ? option.description : '';
    };

    const getOptionIconPath = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('iconPath') ? option.iconPath : '';
    };

    const handleOptionClick = (option) => {
        const value = getOptionValue(option);
        handleFilterChange(filterName, value, filterKey);
        setIsOpen(false);
        if (onBlur) onBlur(filterName);
    };

    const getCurrentSelectedOption = () => {
        return options.find(opt => getOptionValue(opt) === selectedValue) || null;
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (isDisabled) return;  
        
        handleFilterChange(filterName, "", filterKey);
        if (onBlur) onBlur(filterName);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
                if (onBlur) onBlur(filterName);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [filterName, onBlur, selectRef]);

    useEffect(() => {
        if (shouldAutoClearOnMissingOptions && !hasOptions && selectedValue) {
            handleFilterChange(filterName, "", filterKey);
        }
    }, [hasOptions, selectedValue, filterName, filterKey, handleFilterChange, shouldAutoClearOnMissingOptions]);

    const isDark = theme === 'dark';
    const selectedOption = getCurrentSelectedOption();

    const renderOptionContent = (option) => (
        <div className={styles.optionContent}>
            {getOptionIconPath(option) && (
                <img
                    src={getOptionIconPath(option)}
                    alt=""
                    className={styles.optionIcon}
                    loading="lazy"
                    aria-hidden="true"
                />
            )}
            <span className={`${styles.optionLabel} ${isDark ? styles.darkText : ""}`}>{getOptionLabel(option)}</span>
        </div>
    );

    return (
        <div 
            className={`
                ${styles.selectContainer} 
                ${hasError ? styles.errorContainer : ""}
                ${isDisabled ? styles.disabledContainer : ""}
                ${isDark ? styles.darkContainer : ""}
            `}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div 
                className={`
                    ${styles.selectField} 
                    ${hasError ? styles.errorBorder : ""}
                    ${isDisabled ? styles.noDataBorder : ""} 
                    ${isDark ? styles.darkSelectField : ""}
                `} 
                onClick={toggleDropdown}
            >
                <span className={`${styles.selectSelectedValue} ${isDark ? styles.darkText : ""}`}>
                    {
                        selectedOption !== null
                            ? renderOptionContent(selectedOption)
                            : <span className={`${styles.selectPlaceholder} ${isDisabled ? styles.noDataPlaceholder : ''} ${isDark ? styles.darkPlaceholder : ""}`}> 
                                {hasOptions ? t.placeholder : t.noData} 
                              </span>
                    }
                </span>
                <div className={styles.selectIconContainer}>
                    {selectedValue && hasOptions && ( 
                        <div className={styles.selectClearIcon} onClick={handleClear}>
                            <CloseIcon fontSize="inherit" />
                        </div>
                    )}
                    <div className={styles.selectToggleIcon}>
                        {hasOptions && <KeyboardArrowDownIcon fontSize="inherit" />}
                    </div>
                </div>
            </div>
            {isOpen && !isDisabled && hasOptions && ( 
                <ul className={`${styles.optionsList} ${isDark ? styles.darkOptionsList : ""}`} ref={selectRef}>
                    {options.map((option, index) => (
                        <li
                            key={index}
                            className={`
                                ${styles.optionItem} 
                                ${getOptionValue(option) === selectedValue ? styles.selected : ""}
                                ${isDark ? styles.darkOptionItem : ""}
                                ${getOptionValue(option) === selectedValue && isDark ? styles.darkSelected : ""}
                            `}
                            onClick={() => handleOptionClick(option)}
                        >
                            {renderOptionContent(option)}
                            {getOptionDescription(option) && (
                                <div className={`${styles.optionDescription} ${isDark ? styles.darkDesc : ""}`}>({getOptionDescription(option)})</div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {hasError && isMouseEnter && errorMessage && <ErrorPopup message={errorMessage} theme={theme} />}
        </div>
    );
};
